import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ToastMessageService } from '@fitness-force/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import {
  CommunicationCategory,
  CreateCommunicationCategoryRequest,
  UpdateCommunicationCategoryRequest,
  isCommunicationCategoryActive,
} from '../../models';
import { CommunicationCategoryService } from '../../services';
import { FeatureFlagService } from '@fitness-force/shared-utils';
import { AuditTrailMiniComponent } from '@fitness-force/audit-trail';
import { of, switchMap, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'ff-communication-category-form',
  templateUrl: './communication-category-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  providers: [ToastMessageService],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    TextareaModule,
    ToggleSwitchModule,
    TranslateModule,
    AuditTrailMiniComponent,
  ],
})
export class CommunicationCategoryFormComponent implements OnInit, OnDestroy {
  private readonly categoryService = inject(CommunicationCategoryService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly translate = inject(TranslateService);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly destroy$ = new Subject<void>();

  readonly visible = input(false);
  readonly activeCategory = input<CommunicationCategory | null>(null);
  readonly tenantId = input(0);
  readonly changed = output<void>();
  readonly dialogClose = output<void>();

  form!: UntypedFormGroup;
  isSubmitting = signal(false);
  showAuditLogsFeature = false;

  constructor() {
    effect(() => {
      if (this.visible()) {
        this.buildForm(this.activeCategory());
        this.cdr.detectChanges();
      }
    });
  }

  ngOnInit(): void {
    this.featureFlagService.getFlag$('show_audit_trial').pipe(takeUntil(this.destroy$)).subscribe((showAudit) => {
      this.showAuditLogsFeature = showAudit;
      this.cdr.detectChanges();
    });
    this.buildForm(null);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isEditMode(): boolean {
    return this.activeCategory() !== null;
  }

  get nameControl(): AbstractControl | null {
    return this.form?.get('name');
  }

  get descriptionControl(): AbstractControl | null {
    return this.form?.get('description');
  }

  get codeControl(): AbstractControl | null {
    return this.form?.get('code');
  }

  buildForm(category: CommunicationCategory | null): void {
    this.form = this.fb.group({
      name: [category?.name ?? '', [Validators.required, Validators.maxLength(100)]],
      description: [category?.description ?? '', [Validators.maxLength(300)]],
      code: [category?.code ?? '', [Validators.maxLength(50)]],
      status: [category ? isCommunicationCategoryActive(category.status) : true],
    });
  }

  submit(): void {
    if (!this.form.valid) {
      Object.keys(this.form.controls).forEach((field) => {
        this.form.get(field)?.markAsTouched({ onlySelf: true });
      });
      return;
    }

    const raw = this.form.getRawValue();
    const category = this.activeCategory();
    this.isSubmitting.set(true);

    if (category) {
      const payload: UpdateCommunicationCategoryRequest = {
        name: raw.name,
        description: raw.description || '',
        code: raw.code || '',
      };
      const wasActive = isCommunicationCategoryActive(category.status);
      const isActive = !!raw.status;

      this.categoryService
        .updateCategory(category.id, payload)
        .pipe(
          switchMap(() => {
            if (wasActive === isActive) {
              return of(undefined);
            }
            return isActive
              ? this.categoryService.markActive(category.id)
              : this.categoryService.markInactive(category.id);
          }),
        )
        .subscribe({
          next: () => {
            this.isSubmitting.set(false);
            this.translate
              .get('COMMUNICATION_CATEGORIES.FORM.UPDATED_SUCCESS')
              .subscribe((msg) => {
                this.toastMessageService.showSuccess(msg);
              });
            this.resetForm();
            this.changed.emit();
          },
          error: (err: unknown) => {
            this.isSubmitting.set(false);
            this.toastMessageService.showError(err);
          },
        });
      return;
    }

    const code = raw.code?.trim();
    const payload: CreateCommunicationCategoryRequest = {
      name: raw.name,
      description: raw.description || undefined,
      code: code || null,
    };

    this.categoryService.createCategory(payload).subscribe({
      next: () => {
        this.isSubmitting.set(false);
        this.translate
          .get('COMMUNICATION_CATEGORIES.FORM.CREATED_SUCCESS')
          .subscribe((msg) => {
            this.toastMessageService.showSuccess(msg);
          });
        this.resetForm();
        this.changed.emit();
      },
      error: (err: unknown) => {
        this.isSubmitting.set(false);
        this.toastMessageService.showError(err);
      },
    });
  }

  cancel(): void {
    this.resetForm();
    this.dialogClose.emit();
  }

  private resetForm(): void {
    this.form?.reset({
      name: '',
      description: '',
      code: '',
      status: true,
    });
    this.form?.markAsPristine();
    this.form?.markAsUntouched();
  }
}
